/**
 * Custom `isExportProcess` function to replace the one in `@uniformdev/next`
 *
 * This function only takes into account the `UNIFORM_BUILD_MODE` envioronment variable
 * in order to flag the process as export or build.
 *
 * Next build sets `global.__NEXT_DATA__.nextExport` to true when building, not only
 * when exporting. Uniform uses that variable in it's `isExportProcess`, so it **always considers
 * we are exporting**, consuming uniform API for content. This prevents clean builds of the
 * Next app, used in CI pipelines, without dependencies of external environments.
 */
function isExportProcess() {
  return process.env.UNIFORM_BUILD_MODE === 'export'
}

function isLocalDevelopment() {
  return process.env.NODE_ENV !== 'production'
}

function getSiteName() {
  // We're not able to set as undefined the environment variable fro test, for this reason we check the lenght
  if ((process.env.UNIFORM_API_SITENAME || '').length > 0) {
    return process.env.UNIFORM_API_SITENAME || ''
  }

  // Needed for ISR function where we don't have environment variables set in file .env as in the build process
  const [, , , rawSitename, country, siteLanguage = ''] = (process.env.SITE_NAME || '').split('-')

  let sitename = rawSitename
  if (sitename === 'elizawashere') {
    sitename = 'eliza'
  }

  return sitename + country + siteLanguage
}

function getWebsiteDomain() {
  if ((process.env.WEBSITE_DOMAIN || '').length > 0) {
    return process.env.WEBSITE_DOMAIN || ''
  }

  // Needed for ISR function where we don't have environment variables set in file .env as in the build process
  const splitSiteName = getSiteNameProperties(process.env.SITE_NAME || '')
  const environment = splitSiteName.environment === 'prod' ? '' : `${splitSiteName.environment}.`

  return `${environment}www.${splitSiteName.brand}.${getSiteTopLevelDomain(splitSiteName.countryCode)}`
}

function getUniformApiUrl() {
  if ((process.env.UNIFORM_API_URL || '').length > 0) {
    return process.env.UNIFORM_API_URL || ''
  }

  // Needed for ISR function where we don't have environment variables set in file .env as in the build process
  const splitSiteName = getSiteNameProperties(process.env.SITE_NAME || '')
  return `https://${splitSiteName.environment}sundioscstore102.blob.core.windows.net/contentsync`
}

function getPublicationCodes() {
  // Needed for ISR function where we don't have environment variables set in file .env as in the build process

  const uniformSiteName = getSiteName()
  let publicationCodes = ''

  switch (uniformSiteName) {
    case 'sunwebnl':
      publicationCodes = 'SZ,WG'
      break
    case 'sunwebuk':
      publicationCodes = 'UW'
      break
    case 'sunwebbenl':
      publicationCodes = 'BZ,BW'
      break
    case 'sunwebbefr':
      publicationCodes = 'FS,FH'
      break
    case 'sunwebdk':
      publicationCodes = 'KZ,KW'
      break
    case 'sunwebde':
      publicationCodes = 'DW'
      break
    case 'sunwebse':
      publicationCodes = 'QZ,QW'
      break
    case 'sunwebfr':
      publicationCodes = 'FZ,FW'
      break
    case 'airtoursse':
      publicationCodes = 'QA'
      break
    case 'primaveranl':
      publicationCodes = 'PV'
      break
    case 'gogonl':
      publicationCodes = 'JR'
      break
    case 'elizanl':
      publicationCodes = 'EZ'
      break
    case 'elizabe':
      publicationCodes = 'BE'
      break
    case 'elizauk':
      publicationCodes = 'UE'
      break
    default:
      publicationCodes = ''
  }

  return publicationCodes?.split(',').filter(elem => elem) || []
}

function getApiGatewayBaseUrl() {
  if ((process.env.API_GATEWAY_BASE_URL || '').length > 0) {
    return process.env.API_GATEWAY_BASE_URL
  }

  // Needed for ISR function where we don't have environment variables set in file .env as in the build process
  const splitSiteName = getSiteNameProperties(process.env.SITE_NAME || '')

  // Only use ACPT for pre-prod environments.
  // In Test we don't have all products imported in Sitecore sites.
  if (splitSiteName.environment !== 'prod') {
    return 'https://acpt.api.sundiogroup.com'
  }
  return 'https://api.sundiogroup.com'
}

function getIdentityBaseUrl() {
  if ((process.env.IDENTITY_BASE_URL || '').length > 0) {
    return process.env.IDENTITY_BASE_URL
  }

  // Needed for ISR function where we don't have environment variables set in file .env as in the build process
  const splitSiteName = getSiteNameProperties(process.env.SITE_NAME || '')

  // We only use ACPT (see API gateway comment)
  // Environment of Identity needs to match the environment of the API Gateway.
  if (splitSiteName.environment !== 'prod') {
    return 'https://acpt-identity.api.sundiogroup.com'
  }
  return 'https://identity.api.sundiogroup.com'
}

function getAppEnvironment() {
  if ((process.env.APP_ENVIRONMENT || '').length > 0) {
    return process.env.APP_ENVIRONMENT
  }

  // Needed for ISR function where we don't have environment variables set in file .env as in the build process
  const splitSiteName = getSiteNameProperties(process.env.SITE_NAME || '')
  return splitSiteName.environment
}

// From netlify site name returns the site name split by properties.
// For example from sundio-prod-www-sunweb-be-nl returns {environment: "prod", brand: "sunweb", countryCode: "be", languageCode: "nl"}
function getSiteNameProperties(netlifySiteName) {
  const splitSiteName = netlifySiteName.split('-')
  const netlifyLanguageCode = splitSiteName[5]
  let siteLanguageCode = ''

  if (netlifyLanguageCode !== undefined) {
    siteLanguageCode = netlifyLanguageCode
  }

  return {
    environment: splitSiteName[1],
    brand: splitSiteName[3],
    countryCode: splitSiteName[4],
    languageCode: siteLanguageCode,
  }
}

function getSiteTopLevelDomain(countryCode) {
  return countryCode === 'uk' ? 'co.uk' : countryCode
}

function getBrandName() {
  const siteName = process.env.SITE_NAME

  if (!siteName) {
    return 'sunweb'
  }

  if (siteName.includes('eliza')) {
    return 'eliza'
  }

  if (siteName.includes('gogo')) {
    return 'gogo'
  }

  return 'sunweb'
}

module.exports = {
  isExportProcess,
  isLocalDevelopment,
  getSiteName,
  getWebsiteDomain,
  getUniformApiUrl,
  getPublicationCodes,
  getApiGatewayBaseUrl,
  getIdentityBaseUrl,
  getAppEnvironment,
  getBrandName,
}
